/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { AdTracker, DEFAULT_TRACKER } from "./Advertisement";
import { usePlayerController } from "./PlayerControllerContext";

type PlayerAdvertisementTrackerContextValue = {
  tracker: AdTracker;
};

type PlayerAdvertisementCueContextValue = {
  cuePoints: number[];
  havePreroll: boolean;
  havePostroll: boolean;
};

type PlayerAdvertisementProviderProps = {
  children: ReactNode;
};

const PlayerAdvertisementTrackerContext =
  createContext<PlayerAdvertisementTrackerContextValue>(
    {} as PlayerAdvertisementTrackerContextValue,
  );

const PlayerAdvertisementCueContext =
  createContext<PlayerAdvertisementCueContextValue>(
    {} as PlayerAdvertisementCueContextValue,
  );

export const PlayerAdvertisementProvider = (
  props: PlayerAdvertisementProviderProps,
) => {
  const { children } = props;

  const { player } = usePlayerController();

  const [tracker, setTracker] = useState<AdTracker>({ ...DEFAULT_TRACKER });
  const [cuePoints, setCuePoints] = useState<number[]>([]);

  const { havePreroll, havePostroll } = useMemo(() => {
    const havePreroll = cuePoints.includes(0);
    const havePostroll = cuePoints.includes(-1);

    return { cuePoints, havePreroll, havePostroll };
  }, [player, cuePoints]);

  useEffect(() => {
    const unsubscribeTracker = player.onEvent(
      ["admetadataupdate", "adend"],
      () => {
        const currentAdTracker = player.currentAdTracker();

        currentAdTracker && setTracker(currentAdTracker);
      },
    );

    const unsubscribePoints = player.onEvent("adloaded", () => {
      setCuePoints(player.adCuePoints());
    });

    return () => {
      unsubscribeTracker();
      unsubscribePoints();
    };
  }, [player]);

  return (
    <PlayerAdvertisementTrackerContext.Provider value={{ tracker }}>
      <PlayerAdvertisementCueContext.Provider
        value={{ cuePoints, havePreroll, havePostroll }}
      >
        {children}
      </PlayerAdvertisementCueContext.Provider>
    </PlayerAdvertisementTrackerContext.Provider>
  );
};

export const usePlayerAdvertisementTracker =
  (): PlayerAdvertisementTrackerContextValue => {
    const context = useContext(PlayerAdvertisementTrackerContext);

    if (!context) {
      throw new Error("Component beyond PlayerAdvertisementTrackerContext");
    }

    return context;
  };

export const usePlayerAdvertisementCue =
  (): PlayerAdvertisementCueContextValue => {
    const context = useContext(PlayerAdvertisementCueContext);

    if (!context) {
      throw new Error("Component beyond PlayerAdvertisementCueContext");
    }

    return context;
  };
